import React from 'react';
import type { SelectOption, ReservationInfo } from '../../../api/reservationInfo';
import type { CommonErrors, CommonFormValues } from '../../../types/reservationInfoForm';

type Props = {
  values: CommonFormValues;
  errors: CommonErrors;
  settingFlags: ReservationInfo['settingFlags'];
  approvalRequired: boolean;
  departments: SelectOption[];
  projects: SelectOption[];
  onTextChange: (field: keyof CommonFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (
    field: 'chargingDepartmentId' | 'projectId'
  ) => (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled: boolean;
};

const ReservationInfoCommonSection = ({
  values,
  errors,
  settingFlags,
  approvalRequired,
  departments,
  projects,
  onTextChange,
  onSelectChange,
  disabled
}: Props) => {
  const styles = getStyles();
  const destinationRequired = settingFlags.isDestinationBusinessTripRequired || approvalRequired === true;
  const purposeRequired = settingFlags.isPurposeBusinessTripRequired || approvalRequired === true;
  const renderTextInput = (
    field: keyof CommonFormValues,
    label: string,
    placeholder: string,
    isRequired: boolean,
    isShown: boolean
  ) => {
    if (isShown === false) return null;
    const inputId = `reservation-info-${field}`;
    return (
      <div style={styles.formGroup}>
        <label htmlFor={inputId} style={styles.label}>
          {label}
          {isRequired && <span style={styles.required}>*</span>}
        </label>
        <input
          id={inputId}
          type="text"
          style={{
            ...styles.input,
            ...(errors[field] ? styles.inputError : {})
          }}
          value={String(values[field] ?? '')}
          placeholder={placeholder}
          onChange={onTextChange(field)}
          disabled={disabled}
        />
        {errors[field] && <span style={styles.errorText}>{errors[field]}</span>}
      </div>
    );
  };

  const renderSelectInput = (
    field: 'chargingDepartmentId' | 'projectId',
    label: string,
    options: SelectOption[],
    isRequired: boolean,
    isShown: boolean
  ) => {
    if (isShown === false) return null;
    const inputId = `reservation-info-${field}`;
    return (
      <div style={styles.formGroup}>
        <label htmlFor={inputId} style={styles.label}>
          {label}
          {isRequired && <span style={styles.required}>*</span>}
        </label>
        <select
          id={inputId}
          style={{
            ...styles.select,
            ...(errors[field] ? styles.inputError : {})
          }}
          value={values[field] ?? ''}
          onChange={onSelectChange(field)}
          disabled={disabled}
        >
          <option value="">選択してください</option>
          {options.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        {errors[field] && <span style={styles.errorText}>{errors[field]}</span>}
      </div>
    );
  };

  return (
    <>
      {renderTextInput(
        'finalDestination',
        '出張先',
        '出張先を入力してください',
        destinationRequired,
        settingFlags.showDestinationBusinessTrip
      )}
      {renderTextInput(
        'purpose',
        '出張の目的',
        '例：クライアント訪問',
        purposeRequired,
        settingFlags.showPurposeBusinessTrip
      )}
      {renderTextInput(
        'internalNumber',
        '社内管理番号',
        '例：20250116-001',
        settingFlags.isInternalNumberRequired,
        settingFlags.showInternalNumber
      )}
      {renderSelectInput(
        'chargingDepartmentId',
        '費用負担部署',
        departments,
        settingFlags.isChargingDepartmentRequired,
        settingFlags.showChargingDepartment
      )}
      {renderSelectInput(
        'projectId',
        'プロジェクト（案件）',
        projects,
        settingFlags.isProjectNameRequired,
        settingFlags.showProjectName
      )}
    </>
  );
};

const getStyles = (): Record<string, React.CSSProperties> => ({
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '6px',
    color: '#333'
  },
  required: {
    color: '#e74c3c',
    marginLeft: '4px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  inputError: {
    borderColor: '#e74c3c'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  }
});

export default ReservationInfoCommonSection;
